/**
 * The shared shell every report page renders through: header + Print/Download
 * actions, an optional KPI tile row, an optional chart, and a table driven
 * entirely by the envelope's own `columns`/`rows` (`reports.model.md` §5) —
 * one generic renderer instead of six hand-rolled tables, since the whole
 * point of a dynamic `columns` array is that the frontend doesn't need to
 * know each breakdown's shape ahead of time.
 *
 * Print is the browser's own dialog against `@media print` in `app.css`
 * (already hides the sidebar/topbar); Download is a client-side CSV built
 * from the same rows already on screen — see `utils/exportCsv.js` and
 * `reports.model.md` §0 for why neither is the spec's async job.
 */
import {
  PageHeader, Card, ErrorState, EmptyState, MockBadge, SkeletonTable, SkeletonChart, SkeletonKpiRow, StatTile,
} from '../../components/common/index.jsx';
import { money, number, percent } from '../../utils/formatters.js';
import { downloadCsv } from '../../utils/exportCsv.js';
import { isMock } from '../../api/mockFallback.js';

/** A plain `<select>` for a report's breakdown/period picker — every report page uses one of these. */
export function ReportSelect({ value, onChange, options, label }) {
  return (
    <label className="d-flex align-items-center gap-2 t-xs ink-3 no-print">
      {label}
      <select
        className="form-select form-select-sm" style={{ width: 'auto' }}
        value={value} onChange={(e) => onChange(e.target.value)}
      >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}

/** From/To calendar-date filter, the same shape the Feed screen's date filter already uses. */
export function ReportDateRange({ from, to, onFrom, onTo }) {
  return (
    <div className="d-flex align-items-center gap-2 no-print">
      <label className="filter-x">
        <span className="filter-x-label">From</span>
        <input type="date" className="form-control form-control-sm" value={from} max={to || undefined} onChange={(e) => onFrom(e.target.value)} />
      </label>
      <label className="filter-x">
        <span className="filter-x-label">To</span>
        <input type="date" className="form-control form-control-sm" value={to} min={from || undefined} onChange={(e) => onTo(e.target.value)} />
      </label>
    </div>
  );
}

function formatCell(column, value) {
  if (value == null || value === '') return '—';
  if (column.type === 'money') return money(value);
  if (column.type === 'percent') return percent(value);
  if (column.type === 'number') return number(value);
  return String(value);
}

/**
 * @param {{
 *   title:string, subtitle?:string, controls?:React.ReactNode,
 *   isLoading:boolean, error?:Error, onRetry?:()=>void, data?:object,
 *   summaryTiles?:{key:string,label:string,value:string,tone?:string,icon?:string,meta?:string}[],
 *   chart?:React.ReactNode, filename:string, emptyMessage?:string,
 * }} props
 */
export function ReportView({
  title, subtitle, controls, isLoading, error, onRetry, data,
  summaryTiles = [], chart, filename, emptyMessage = 'Nothing to show for this range yet.',
}) {
  const columns = data?.columns ?? [];
  const rows = data?.rows ?? [];
  const canExport = !isLoading && !error && rows.length > 0;

  return (
    <>
      <PageHeader title={title} subtitle={subtitle} badge={isMock(data) ? <MockBadge section={filename} /> : null}>
        {controls}
        <button
          type="button" className="btn btn-soft btn-sm-x no-print"
          onClick={() => window.print()} disabled={!canExport}
        >
          <i className="bi bi-printer me-1" aria-hidden="true" />Print
        </button>
        <button
          type="button" className="btn btn-soft btn-sm-x no-print"
          onClick={() => downloadCsv(columns, rows, filename)} disabled={!canExport}
        >
          <i className="bi bi-download me-1" aria-hidden="true" />Download CSV
        </button>
      </PageHeader>

      {(summaryTiles.length > 0) && (
        isLoading ? <SkeletonKpiRow count={summaryTiles.length || 4} /> : (
          <div className="row g-3 mb-3">
            {summaryTiles.map((t) => (
              <div className="col-6 col-lg-3" key={t.key}>
                <StatTile label={t.label} value={t.value} tone={t.tone} icon={t.icon} meta={t.meta} />
              </div>
            ))}
          </div>
        )
      )}

      {chart && (
        <Card className="mb-3 no-print" bodyClass="pt-3">
          {isLoading ? <SkeletonChart /> : chart}
        </Card>
      )}

      <Card bodyClass="p-0" className="report-print-area">
        {isLoading ? (
          <div className="p-3"><SkeletonTable columns={columns.map((c) => ({ key: c.key, header: c.label }))} /></div>
        ) : error ? (
          <div className="p-4"><ErrorState error={error} onRetry={onRetry} /></div>
        ) : rows.length === 0 ? (
          <div className="p-4"><EmptyState icon="bi-bar-chart" title="No data" message={emptyMessage} /></div>
        ) : (
          <div className="table-x-wrap">
            <table className="table-x">
              <thead>
                <tr>
                  {columns.map((c) => (
                    <th key={c.key} className={c.type !== 'text' ? 'text-end' : undefined}>{c.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <tr key={row.label ? `${row.label}-${i}` : i}>
                    {columns.map((c) => (
                      <td key={c.key} className={c.type !== 'text' ? 'text-end' : undefined}>
                        {formatCell(c, row[c.key])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
